/**
 * Auth Store
 * Global authentication state management with Zustand
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthState, User, LoginRequest, RegisterRequest } from '@/types/auth';
import * as authApi from '@/api/auth';
import { getApiError } from '@/api/client';

interface AuthStore extends AuthState {
  // Actions
  login: (credentials: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  verifyTwoFactor: (code: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  clearError: () => void;
  setToken: (token: string) => void;
  setUser: (user: User) => void;
}

const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      // Initial state
      status: 'idle',
      user: null,
      token: null,
      tempToken: null,
      error: null,

      // Login action
      login: async (credentials: LoginRequest) => {
        set({ status: 'loading', error: null });

        try {
          const response = await authApi.login(credentials);
          console.log('Login response:', response);

          // Check if 2FA is required
          if (response.requiresTwoFactor && response.tempToken) {
            set({
              status: 'requires2fa',
              tempToken: response.tempToken,
              error: null,
            });
            return;
          }

          // Backend uses session cookies, not JWT tokens
          // Success if we get a user back (session cookie is set automatically)
          if (response.user) {
            set({
              status: 'authenticated',
              token: 'session', // Placeholder since we use cookies
              user: response.user,
              tempToken: null,
              error: null,
            });
          } else {
            console.error('Invalid login response structure:', response);
            throw new Error('Invalid login response');
          }
        } catch (error) {
          const message = getApiError(error);
          set({
            status: 'unauthenticated',
            error: message,
            token: null,
            user: null,
            tempToken: null,
          });
          throw error;
        }
      },

      // Register action
      register: async (data: RegisterRequest) => {
        set({ status: 'loading', error: null });

        try {
          const response = await authApi.register(data);

          // Backend uses session cookies
          if (response.user) {
            set({
              status: 'authenticated',
              token: 'session', // Placeholder since we use cookies
              user: response.user,
              tempToken: null,
              error: null,
            });
          } else {
            throw new Error('Invalid register response');
          }
        } catch (error) {
          const message = getApiError(error);
          set({
            status: 'unauthenticated',
            error: message,
            token: null,
            user: null,
          });
          throw error;
        }
      },

      // Verify 2FA code
      verifyTwoFactor: async (code: string) => {
        const { tempToken } = get();
        if (!tempToken) {
          throw new Error('No temporary token available');
        }

        set({ status: 'loading', error: null });

        try {
          const response = await authApi.verifyTwoFactor({ tempToken, code });

          // Backend uses session cookies
          if (response.user) {
            set({
              status: 'authenticated',
              token: 'session', // Placeholder since we use cookies
              user: response.user,
              tempToken: null,
              error: null,
            });
          } else {
            throw new Error('Invalid 2FA response');
          }
        } catch (error) {
          const message = getApiError(error);
          set({
            status: 'requires2fa',
            error: message,
          });
          throw error;
        }
      },

      // Logout action
      logout: async () => {
        try {
          await authApi.logout();
        } catch (error) {
          // Ignore logout errors, clear state anyway
          console.error('Logout error:', error);
        } finally {
          set({
            status: 'unauthenticated',
            user: null,
            token: null,
            tempToken: null,
            error: null,
          });
        }
      },

      // Refresh user profile
      refreshProfile: async () => {
        const { token } = get();
        if (!token) return;

        try {
          const user = await authApi.getProfile();
          set({ user });
        } catch (error) {
          console.error('Failed to refresh profile:', error);
          // Don't clear auth on profile refresh failure
        }
      },

      // Clear error
      clearError: () => {
        set({ error: null });
      },

      // Set token (for manual token management)
      setToken: (token: string) => {
        set({ token, status: 'authenticated' });
      },

      // Set user (for manual user management)
      setUser: (user: User) => {
        set({ user });
      },
    }),
    {
      name: 'auth-storage',
      // Only persist token and user, not loading/error states
      partialize: (state) => ({
        token: state.token,
        user: state.user,
        status: state.token ? 'authenticated' : 'idle',
      }),
    }
  )
);

export default useAuthStore;
